import { Button, Card, Col, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';
import SpinnerBlock from 'components/others/SpinnerBlock';
import { useTaskDashboard } from './model';

const TaskDashboard = () => {
    const { t } = useTranslation(['Dashboard', 'Tasks']);
    const { enabled, canViewProcesses, data, loading, retry } = useTaskDashboard();
    if (!enabled) return null;
    const tasks = data?.tasks;
    const processes = data?.processes;
    const statuses = [
        { status: TaskTypeStatus.NEW, variant: 'primary' },
        { status: TaskTypeStatus.INWORK, variant: 'info' },
        { status: TaskTypeStatus.COMPLETED, variant: 'warning' },
        { status: TaskTypeStatus.ACCEPTED, variant: 'success' },
        { status: TaskTypeStatus.CANCELLED, variant: 'secondary' },
        { status: TaskTypeStatus.REJECT, variant: 'danger' },
    ];

    return (
        <Col xs={12} xl={6} className="d-flex">
            <Card className="w-100 dashboard-summary-panel mb-0">
                <Card.Header className="d-flex align-items-center gap-3">
                    <span className="avtar avtar-s bg-light-primary text-primary rounded" aria-hidden="true">
                        <SolarIcon name="card-checklist" />
                    </span>
                    <h5 className="mb-0">{t('panels.tasksTitle')}</h5>
                </Card.Header>
                <Card.Body className="position-relative d-flex flex-column" aria-busy={loading}>
                    <SpinnerBlock show={loading} />
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <h6 className="mb-0">{t('panels.tasks')}</h6>
                        <Link to="/tasks" className="dashboard-summary-panel__link text-primary">{t('panels.openRegistry')}<SolarIcon name="arrow-right" /></Link>
                    </div>
                    <div className="dashboard-summary-panel__metrics bg-body-tertiary rounded p-2 align-items-center">
                        {[
                            { key: 'total', value: tasks?.total, variant: 'primary', icon: 'card-checklist' },
                            { key: 'today', value: tasks?.today, variant: 'info', icon: 'calendar3' },
                            { key: 'overdue', value: tasks?.overdue, variant: 'danger', icon: 'clock-history' },
                        ].map(({ key, value, variant, icon }) => (
                            <div key={key} className={`dashboard-summary-panel__metric rounded p-3 ${key === 'total' ? 'bg-light-primary' : ''}`}>
                                <span className={`text-${variant} d-inline-flex mb-2 f-20`} aria-hidden="true">
                                    <SolarIcon name={icon} />
                                </span>
                                <strong className={`d-block dashboard-summary-panel__number text-${variant}`}>{value ?? '—'}</strong>
                                <span className="text-muted d-block mt-1 f-12">{t(`panels.${key}`)}</span>
                            </div>
                        ))}
                    </div>
                    <ProgressBar className="mt-4 mb-3 overflow-hidden">
                        {statuses.map(({ status, variant }) => (
                            <ProgressBar
                                key={status}
                                className="rounded-0"
                                now={tasks?.total ? (tasks.byStatus[status] ?? 0) / tasks.total * 100 : 0}
                                variant={variant}
                                aria-label={t(`Tasks:status.${status}`)}
                                aria-valuetext={tasks ? String(tasks.byStatus[status] ?? 0) : '—'}
                            />
                        ))}
                    </ProgressBar>
                    <dl className="dashboard-summary-panel__statuses mb-4">
                        {statuses.map(({ status, variant }) => (
                            <div key={status}>
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                    <dt className="text-body fw-normal d-flex align-items-center gap-2">
                                        <span className={`dashboard-summary-panel__dot bg-${variant}`} aria-hidden="true" />
                                        {t(`Tasks:status.${status}`)}
                                    </dt>
                                    <dd className={`badge bg-light-${variant} text-${variant} mb-0`}>{tasks ? tasks.byStatus[status] ?? 0 : '—'}</dd>
                                </div>
                            </div>
                        ))}
                    </dl>
                    {!loading && !tasks && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3" role="status">
                            <span className="text-danger">{t('panels.unavailable')}</span>
                            <Button size="sm" variant="outline-primary" onClick={retry}>{t('panels.retry')}</Button>
                        </div>
                    )}
                    {canViewProcesses && (
                        <div className="border-top mt-auto pt-4">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                <h6 className="mb-0">{t('panels.myProcesses')}</h6>
                                <Link to="/tasks/processes" className="dashboard-summary-panel__link text-primary">{t('panels.openRegistry')}<SolarIcon name="arrow-right" /></Link>
                            </div>
                            <dl className="dashboard-summary-panel__metrics mb-0">
                                {([
                                    { status: 'draft', variant: 'secondary', icon: 'file-earmark-text' },
                                    { status: 'active', variant: 'primary', icon: 'hourglass-split' },
                                    { status: 'done', variant: 'success', icon: 'check-circle' },
                                ] as const).map(({ status, variant, icon }) => (
                                    <div key={status} className="dashboard-summary-panel__metric">
                                        <span className={`avtar avtar-xs bg-light-${variant} text-${variant} rounded-circle mb-2`} aria-hidden="true">
                                            <SolarIcon name={icon} />
                                        </span>
                                        <dt className="text-muted fw-normal f-12 mb-1">{t(`panels.process.${status}`)}</dt>
                                        <dd className="h4 text-body mb-0">{processes?.[status] ?? '—'}</dd>
                                    </div>
                                ))}
                            </dl>
                            {!loading && !processes && (
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3" role="status">
                                    <span className="text-danger">{t('panels.unavailable')}</span>
                                    <Button size="sm" variant="outline-primary" onClick={retry}>{t('panels.retry')}</Button>
                                </div>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );
};

export default TaskDashboard;
